import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import type { Project } from "../api/types";
import { outsideOf, useDismissLayer } from "../lib/dismissLayers";
import { PHONE_QUERY, useMediaQuery } from "../lib/useMediaQuery";
import { filterProjects, foldForSearch as fold, PROJECT_SEARCH_THRESHOLD, projectStateWords as lifecycleWords } from "../lib/projectIcon";
import { ProjectIcon } from "./ProjectIcon";

type PickerProject = Pick<Project, "key" | "name" | "lifecycle">;

const TYPE_AHEAD_MS = 700;

export interface ProjectPickerProps {
  /** Id of the button (the label above it points at it). */
  id: string;
  projects: readonly PickerProject[];
  /** The project the shell is on ("" for none). */
  value: string;
  onSelect: (key: string) => void;
  loading: boolean;
  /** Id of the label element above the button. */
  labelId: string;
  /** Id of a help line (e.g. "Projects could not be loaded"). */
  describedBy?: string;
}

/**
 * Batch 18 GRF-47 (B18-UX-SPECS §4.5–4.7): the sidebar project picker. A button with the project's icon, key and name opens a
 * listbox — a floating panel on a desktop, a bottom sheet below 760 px. Each row: icon, key, name; the current project ticked.
 * Keyboard: ↑/↓, Home/End, type-ahead on the key, Enter selects, Esc closes and gives focus back to the button. Tab closes.
 * It is a layer of the shared dismiss stack, so a click outside closes it.
 */
export function ProjectPicker({ id: buttonId, projects, value, onSelect, loading, labelId, describedBy }: ProjectPickerProps) {
  const phone = useMediaQuery(PHONE_QUERY);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState<string | null>(null);
  const [place, setPlace] = useState<CSSProperties | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const typed = useRef({ text: "", at: 0 });
  const listId = useId();
  const searchId = useId();
  const searchable = projects.length > PROJECT_SEARCH_THRESHOLD;
  const shown = useMemo(() => (searchable ? filterProjects(projects, query) : [...projects]), [projects, query, searchable]);
  const activeKey = active !== null && shown.some((p) => p.key === active) ? active : (shown[0]?.key ?? null);
  const optionId = (key: string) => `${listId}-${key}`;
  const current = projects.find((p) => p.key === value);
  const disabled = projects.length === 0;

  const close = (refocus: boolean) => {
    setOpen(false);
    setPlace(null);
    if (refocus) buttonRef.current?.focus();
  };

  const openList = () => {
    setQuery("");
    setActive(value || null);
    setOpen(true);
  };

  const choose = (key: string) => {
    close(true);
    if (key !== value) onSelect(key);
  };

  useDismissLayer(open, {
    element: () => rootRef.current,
    isOutside: (event) => outsideOf(event, rootRef.current, panelRef.current),
    dismiss: (reason) => close(reason === "escape"),
  });

  // Desktop: a floating panel under the button, fixed to the viewport so the side nav's own scrolling never clips it.
  useLayoutEffect(() => {
    if (!open || phone) return undefined;
    const measure = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = Math.min(Math.max(rect.width, 320), window.innerWidth - 24);
      const left = Math.min(Math.max(rect.left, 12), Math.max(12, window.innerWidth - width - 12));
      const top = rect.bottom + 6;
      setPlace({ position: "fixed", top, left, width, maxWidth: "none", maxHeight: Math.max(160, Math.min(440, window.innerHeight - top - 12)) });
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open, phone]);

  // Focus moves into the list once it can take focus: on a desktop the panel stays hidden until it has been placed under the
  // button (a hidden element cannot be focused), so this waits for the placement.
  const placed = phone || place !== null;
  useEffect(() => {
    if (!open || !placed) return;
    if (panelRef.current?.contains(document.activeElement)) return;
    (searchable ? inputRef.current : listRef.current)?.focus();
  }, [open, placed, searchable]);

  useEffect(() => {
    if (!open || activeKey === null) return;
    document.getElementById(optionId(activeKey))?.scrollIntoView?.({ block: "nearest" });
    // optionId is derived from listId, which never changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeKey]);

  const move = (to: number) => {
    if (shown.length === 0) return;
    const target = shown[Math.min(Math.max(to, 0), shown.length - 1)];
    if (target) setActive(target.key);
  };
  const at = activeKey === null ? -1 : shown.findIndex((p) => p.key === activeKey);

  function onListKeyDown(event: KeyboardEvent<HTMLElement>, inSearch: boolean) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        move(at < 0 ? 0 : at + 1);
        return;
      case "ArrowUp":
        event.preventDefault();
        move(at < 0 ? shown.length - 1 : at - 1);
        return;
      case "Home":
      case "End":
        if (inSearch) return; // the caret keeps its own Home/End in the search field
        event.preventDefault();
        move(event.key === "Home" ? 0 : shown.length - 1);
        return;
      case "Enter":
        event.preventDefault();
        if (activeKey !== null) choose(activeKey);
        return;
      case "Escape":
        event.preventDefault();
        close(true);
        return;
      case "Tab":
        close(false);
        return;
      default:
        break;
    }
    if (inSearch || event.key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) return;
    // Type-ahead on the key: letters typed within a moment of each other build one prefix ("E", "V" → EVP).
    event.preventDefault();
    const now = Date.now();
    const text = (now - typed.current.at < TYPE_AHEAD_MS ? typed.current.text : "") + event.key;
    typed.current = { text, at: now };
    const needle = fold(text);
    const hit = shown.find((p) => fold(p.key).startsWith(needle)) ?? shown.find((p) => fold(p.name).startsWith(needle));
    if (hit) setActive(hit.key);
  }

  const panel = open ? (
    <div
      ref={panelRef}
      className={phone ? "gf-pop-panel sheet gf-projpicker" : "gf-pop-panel start gf-projpicker"}
      style={phone ? undefined : (place ?? { position: "fixed", visibility: "hidden" })}
      role="group"
      aria-label="Choose a project"
    >
      {searchable && (
        <div className="gf-projpicker-search">
          <label htmlFor={searchId}>Find a project</label>
          <input
            ref={inputRef}
            id={searchId}
            type="search"
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={activeKey !== null ? optionId(activeKey) : undefined}
            autoComplete="off"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActive(null);
            }}
            onKeyDown={(event) => onListKeyDown(event, true)}
          />
        </div>
      )}
      <ul
        ref={listRef}
        id={listId}
        role="listbox"
        aria-label="Projects"
        tabIndex={searchable ? -1 : 0}
        aria-activedescendant={!searchable && activeKey !== null ? optionId(activeKey) : undefined}
        onKeyDown={searchable ? undefined : (event) => onListKeyDown(event, false)}
        className="gf-projpicker-list"
      >
        {shown.map((p) => {
          const state = lifecycleWords(p.lifecycle);
          return (
            <li
              key={p.key}
              id={optionId(p.key)}
              role="option"
              aria-selected={p.key === value}
              className={p.key === activeKey ? "active" : undefined}
              onMouseMove={() => {
                if (p.key !== activeKey) setActive(p.key);
              }}
              onClick={() => choose(p.key)}
            >
              <ProjectIcon projectKey={p.key} muted={state !== null} />
              <b className="gf-projpicker-key">{p.key}</b>{" "}
              <span className="gf-projpicker-name">{p.name}</span>
              {state && <> <span className="gf-projpicker-state">{state}</span></>}
              <span className="gf-projpicker-tick" aria-hidden="true">
                {p.key === value ? "✓" : ""}
              </span>
            </li>
          );
        })}
      </ul>
      {shown.length === 0 && (
        <p className="help gf-projpicker-none" role="status">
          No project matches &ldquo;{query.trim()}&rdquo;.
        </p>
      )}
    </div>
  ) : null;

  return (
    <div className="gf-projpicker-root" ref={rootRef}>
      <button
        ref={buttonRef}
        id={buttonId}
        type="button"
        className="gf-projpicker-button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={`${labelId} ${buttonId}-value`}
        aria-describedby={describedBy}
        disabled={disabled}
        onClick={() => (open ? close(true) : openList())}
        onKeyDown={(event) => {
          if (!open && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
            event.preventDefault();
            openList();
          }
        }}
      >
        {current ? (
          <>
            <ProjectIcon projectKey={current.key} muted={lifecycleWords(current.lifecycle) !== null} />
            <span className="gf-projpicker-current" id={`${buttonId}-value`}>
              <b>{current.key}</b> <span className="gf-projpicker-name">{current.name}</span>
            </span>
          </>
        ) : (
          <span className="gf-projpicker-current" id={`${buttonId}-value`}>
            {disabled ? (loading ? "Loading…" : "No projects") : "Choose a project"}</span>
        )}
        <svg className="gf-projpicker-chevron" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" focusable="false">
          <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {panel && createPortal(panel, document.body)}
    </div>
  );
}
